import * as React from 'react';
import isEqual from 'react-fast-compare';

import useWindowSize from '@/hooks/useWindowSize';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getMostPopularItemsByBids } from '@/redux/modules/filteredItems';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import {
    getItemSummaryCatalogStatus,
    getItemSummarySellerId,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getItemFacets } from '@/redux/modules/itemFacets';
import { fetchMoreSearchResults, submitSearch } from '@/redux/modules/search/search.actions';
import { getSearchedItemIds, getSearchUiState } from '@/redux/modules/search/search.selectors';
import { fetchMoreSimilarItems, getMoreResultsAvailable, getSimilarItemIds } from '@/redux/modules/similarItems';
import { CategoryLink } from '@/types/CategoryLink';
import { ItemFacets } from '@/types/ItemFacets';
import { Suggestions } from '@/types/SimilarItemInfo';

import { SimilarItems } from './SimilarItems';

type Props = {
    categories?: boolean;
    categoryLinks?: CategoryLink[];
    centerHeader?: boolean;
    className?: string;
    itemId: number;
    suggestions?: Suggestions[];
    title?: React.ReactNode;
};

const SIMILAR_ITEMS = 'similarItems';

const SimilarItemsContainer = ({
    categories,
    categoryLinks = [],
    centerHeader = false,
    className,
    itemId,
    suggestions = [],
    title,
}: Props) => {
    const dispatch = useAppDispatch();

    const [catIds, setCatIds] = React.useState([]);
    const [name, setName] = React.useState(SIMILAR_ITEMS);
    const [page, setPage] = React.useState(1);

    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const itemTitle = useAppSelector((state) => getItemSummaryTitle(state, itemId));

    const { width } = useWindowSize();
    const facets: ItemFacets = useAppSelector((state) => getItemFacets(state, itemId));

    const similarItemIds = useAppSelector((state) => getSimilarItemIds(state, itemId));
    const categoryIds = categories && facets.categories ? facets.categories.map((i) => i.categoryId) : [];
    const moreSimilarResultsAvailable = useAppSelector(getMoreResultsAvailable);

    const tagItemIds = useAppSelector(getSearchedItemIds);
    const { isLoading, totalPages } = useAppSelector(getSearchUiState);

    React.useEffect(() => {
        dispatch(
            fetchItemSummariesIfNeeded({
                identifier: 'SIMILAR-ITEMS-CONTAINER',
                itemIds: tagItemIds,
            })
        );
    }, [dispatch, tagItemIds]);

    const popularItemIds = useAppSelector((state) => getMostPopularItemsByBids(state, sellerId));

    const getMoreSimilarItems = () => {
        if (name === SIMILAR_ITEMS) {
            dispatch(
                fetchMoreSimilarItems({
                    categoryIds,
                    itemId,
                    sellerId: sellerId,
                    title: itemTitle,
                })
            );
        } else {
            let nextPage = page + 1;
            dispatch(
                fetchMoreSearchResults({
                    pageName: 'item',
                    searchQuery: {
                        categories: catIds,
                        options: { status: ['upcoming', 'live', 'online'] },
                        page: nextPage,
                        pageSize: 12,
                    },
                })
            );
            setPage(nextPage);
        }
    };

    const getTagItems = (categoryIds: any[], title: string) => {
        dispatch(
            submitSearch({
                pageName: 'item',

                searchQuery: {
                    categories: categoryIds,
                    options: { status: ['upcoming', 'live', 'online'] },
                    page: 1,
                    pageSize: 12,
                },
            })
        );

        setCatIds(categoryIds);
        setName(title);
        setPage(1);
    };

    const selectedItems = name !== SIMILAR_ITEMS ? tagItemIds : similarItemIds;
    const moreResultsAvailable = name !== SIMILAR_ITEMS ? page < totalPages : moreSimilarResultsAvailable;

    return (
        <SimilarItems
            catalogStatus={catalogStatus}
            categoryLinks={categoryLinks}
            centerHeader={centerHeader}
            className={className}
            facets={facets}
            getMoreSimilarItems={getMoreSimilarItems}
            getTagItems={getTagItems}
            isLoading={isLoading && name !== SIMILAR_ITEMS}
            itemTitle={itemTitle}
            moreResultsAvailable={moreResultsAvailable}
            popularItemIds={popularItemIds}
            selectedItems={selectedItems}
            suggestions={suggestions}
            title={title}
            width={width}
        />
    );
};

export default React.memo<Props>(SimilarItemsContainer, isEqual);
